import { Core } from "@faire/design-tokens";
import { FileUploader } from "@faire/help-center-self-serve/components/FileUploader/FileUploader";
import { Column, FormField } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";

const TEN_MEGA_BYTES = 10 * 1024 * 1024;

interface IAttachmentProps {
  attachment?: File;
  setAttachment: (attachment?: File) => void;
}

export const AttachmentFormField: React.FC<IAttachmentProps> = ({
  attachment,
  setAttachment,
}) => {
  const [attachmentError, setAttachmentError] = useState<string | undefined>(
    undefined
  );
  const handleFileSelected = (file: File) => {
    setAttachmentError(undefined);
    setAttachment(file);
    return Promise.resolve();
  };

  const clearAttachment = () => {
    setAttachmentError(undefined);
    setAttachment(undefined);
  };

  return (
    <FormField $name="attachment" style={{ width: "100%" }}>
      <Typography {...(attachmentError ? { color: Core.text.critical } : {})}>
        <AttachmentLabel />
      </Typography>
      <Spacer height="1x" />
      <FileUploader
        currentFile={attachment}
        onUploadSuccess={handleFileSelected}
        onUploadError={setAttachmentError}
        onRemoveFile={clearAttachment}
        uploadCta={<AttachmentInstructions />}
        accepts=".pdf, .jpg, .jpeg, .png"
        maxFileSize={TEN_MEGA_BYTES}
        showFileIcon={false}
        height={150}
      />
      <Spacer height="1x" />
      <Typography color={Core.text.subdued} variant="labelSansRegular">
        <HelperMessage />
      </Typography>
    </FormField>
  );
};

const AttachmentLabel = () => (
  <StrictLocalMsg
    defaultMessage="Attachment (optional)"
    description={{
      img: "https://cdn.faire.com/fastly/2e0206e5167f1b5c94f277fe57198d6db46dc343c95e20b7910ed810480abf74.png",
    }}
  />
);

const HelperMessage = () => (
  <StrictLocalMsg
    defaultMessage="Upload a photo or screenshot of the issue if needed"
    description={{
      text: "Helper message for attachment field",
    }}
  />
);

const AttachmentInstructions = () => {
  return (
    <Column gap={5}>
      <Typography align="center" color={Core.text.default}>
        <StrictLocalMsg
          defaultMessage="Drag and drop here or <u>upload a file</u>"
          description={{
            text: "Text inside uploader widget telling user to use widget to attach a file",
          }}
        />
      </Typography>
      <Typography align="center" color="inherit" variant="labelSansRegular">
        <StrictLocalMsg
          defaultMessage="Accepts JPG, PNG, or PDF up to 10MB"
          description={{
            text: "Text inside uploader widget telling user the types of files allowed",
            img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
          }}
        />
      </Typography>
    </Column>
  );
};
